import { useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { Header } from "./components/Header";
import { NavSidebar } from "./components/NavSidebar";
import { useAuth } from "./hooks/useAuth";
import { useEnvironment } from "./hooks/useEnvironment";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { AttributionPage } from "./pages/AttributionPage";
import { BillingHealthPage } from "./pages/BillingHealthPage";
import { BrandHealthPage } from "./pages/BrandHealthPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { MailTriagePage } from "./pages/MailTriagePage";
import { MetricsPage } from "./pages/MetricsPage";
import { ModelsPage } from "./pages/ModelsPage";
import { PromptsPage } from "./pages/PromptsPage";
import { UsersPage } from "./pages/UsersPage";

export function App() {
  const { user, loading, error, signIn, signOut, isAdmin } = useAuth();
  // env drives the key on the content container — switching env unmounts all
  // page components and clears their cached server state.
  const env = useEnvironment();
  // V2M-DEV-610: off-canvas nav drawer state (mobile only — `NavSidebar` is
  // always visible at `md:` and up regardless of this flag). Opened by
  // Header's hamburger button, closed by the drawer's own backdrop/nav-link
  // clicks.
  const [navOpen, setNavOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-slate-600 text-lg">Loading...</div>
      </div>
    );
  }

  // Access denied - user tried to sign in but is not an admin
  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-red-600 text-lg mb-4">
            You are not authorized to use this admin panel.
          </p>
          <p className="text-slate-600">
            This panel is restricted to authorized administrators only.
          </p>
          <button
            onClick={signOut}
            className="mt-6 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            type="button"
          >
            Return to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">VoiceToMail Admin</h1>
            <p className="text-slate-600">Sign in with your admin Google account</p>
          </div>
          {error && (
            <div
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
              role="alert"
            >
              {error}
            </div>
          )}
          <button
            onClick={signIn}
            type="button"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="flex flex-col h-screen">
        <Header userEmail={user.email} onSignOut={signOut} onMenuClick={() => setNavOpen(true)} />
        <div className="flex flex-1 overflow-hidden">
          <NavSidebar open={navOpen} onClose={() => setNavOpen(false)} />
          <main className="flex-1 overflow-auto bg-slate-50">
            {/* key={env} forces a full re-mount of all pages on env switch,
                clearing all in-component cached server state. */}
            <div className="p-3 sm:p-6" key={env}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ErrorBoundary>
                      <MetricsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ErrorBoundary>
                      <AnalyticsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/attribution"
                  element={
                    <ErrorBoundary>
                      <AttributionPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/prompts"
                  element={
                    <ErrorBoundary>
                      <PromptsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/models"
                  element={
                    <ErrorBoundary>
                      <ModelsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ErrorBoundary>
                      <BillingHealthPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ErrorBoundary>
                      <UsersPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/mail-triage"
                  element={
                    <ErrorBoundary>
                      <MailTriagePage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/campaigns"
                  element={
                    <ErrorBoundary>
                      <CampaignsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/brand-health"
                  element={
                    <ErrorBoundary>
                      <BrandHealthPage />
                    </ErrorBoundary>
                  }
                />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}
